import { FuelTypography } from '@frete.com/fuel-react';
import { useDeviceSize } from 'hooks/useDeviceSize';
import React, { FC, useEffect, useRef, useState } from 'react';
import * as Styled from './styled';

export interface ICarousel {
  height: number;
  images: string[];
}

const containerSize = 100;

const Carousel: FC<ICarousel> = ({ images, height }) => {
  const { isMobile } = useDeviceSize();

  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const [imagesMarginLeft, setImagesMarginLeft] = useState<number>(0);
  const [showControls, setShowControls] = useState(false);

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [mouseDownPositionStarted, setMouseDownPositionStarted] = useState<number>(0);
  const [mouseDragDifference, setMouseDragDifference] = useState<number>(0);

  const sliderRef = useRef(null);

  const handlePreviousPicture = () => {
    /* istanbul ignore else */
    if (activeItemIndex !== 0) {
      setActiveItemIndex(activeItemIndex - 1);
    }
  };

  const handleNextPicture = () => {
    /* istanbul ignore else */
    if (activeItemIndex !== images.length - 1) {
      setActiveItemIndex(activeItemIndex + 1);
    }
  };

  const getContainerWidth = () => sliderRef.current.offsetWidth;

  const reset = () => {
    setMouseDragDifference(0);
    setMouseDownPositionStarted(0);
    setIsMouseDown(false);
  };

  const handleChangeOnDotsClick = (itemClickedIndex: number) => {
    setActiveItemIndex(itemClickedIndex);
  };

  const handleImagePosition = () => {
    const marginLeft = containerSize * activeItemIndex * -1;
    setImagesMarginLeft(marginLeft);
  };

  const handleDragFinish = () => {
    reset();
    /* istanbul ignore else */
    if (mouseDragDifference < 0 && activeItemIndex + 1 <= images.length - 1) {
      setActiveItemIndex(activeItemIndex + 1);
      return;
    }

    /* istanbul ignore else */
    if (mouseDragDifference > 0 && activeItemIndex - 1 >= 0) {
      setActiveItemIndex(activeItemIndex - 1);
      return;
    }

    handleImagePosition();
  };

  const handleDragMove = () => {
    /* istanbul ignore else */
    const actualPosition = containerSize * activeItemIndex * -1;
    const porcentageMoved = (containerSize * mouseDragDifference) / getContainerWidth();
    setImagesMarginLeft(actualPosition + porcentageMoved);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    setIsMouseDown(true);
    setMouseDownPositionStarted(event.clientX);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    /* istanbul ignore else */
    if (isMouseDown) {
      setMouseDragDifference(event.clientX - mouseDownPositionStarted);
      handleDragMove();
    }
  };

  const handleTouchDown = (event: React.TouchEvent) => {
    setIsMouseDown(true);
    setMouseDownPositionStarted(event.touches[0]?.clientX);
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    /* istanbul ignore else */
    if (isMouseDown) {
      setMouseDragDifference(event.touches[0].clientX - mouseDownPositionStarted);
      handleDragMove();
    }
  };

  useEffect(() => {
    if (images.length > 1) {
      setShowControls(true);
    }
  }, [images]);

  useEffect(() => {
    handleImagePosition();
  }, [activeItemIndex]);

  return (
    <Styled.CarouselWrapper height={height} hasMultipleImages={showControls} data-testid="carousel-wrapper">
      <Styled.Backdrop
        ref={sliderRef}
        data-testid="dragable-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleDragFinish}
        onTouchStart={handleTouchDown}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleDragFinish}
      />

      <Styled.CounterBadge>
        <FuelTypography size="caption" weight="bold" color="neutral-inverted">
          {activeItemIndex + 1}/{images.length}
        </FuelTypography>
      </Styled.CounterBadge>
      {showControls && !isMobile && (
        <>
          <Styled.PreviousButton
            trailingIcon="chevron-left"
            size="small"
            variant="secondary"
            onlyIcon
            inverted
            onClick={() => handlePreviousPicture()}
            disabled={activeItemIndex === 0}
            data-testid="previous-button"
          />
          <Styled.NextButton
            trailingIcon="chevron-right"
            variant="secondary"
            size="small"
            onlyIcon
            inverted
            onClick={() => handleNextPicture()}
            disabled={activeItemIndex === images.length - 1}
            data-testid="next-button"
          />
        </>
      )}
      {showControls && (
        <Styled.DotsWrapper>
          {images?.map((image, index) => (
            <Styled.Dot
              key={`${image}-dots-${index + 1}`}
              isActive={index === activeItemIndex}
              onClick={() => handleChangeOnDotsClick(index)}
              data-testid="carousel-dots-item"
            />
          ))}
        </Styled.DotsWrapper>
      )}
      <Styled.ImagesWrapper marginLeft={imagesMarginLeft} isMouseDown={isMouseDown} data-testid="images-wrapper">
        {images?.map((image, index) => (
          <Styled.Image key={`${image}-img-${index + 1}`} src={image} alt="Caminhão" data-testid="carousel-image" />
        ))}
      </Styled.ImagesWrapper>
    </Styled.CarouselWrapper>
  );
};

export default Carousel;
